import moment from 'moment';
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect, RouteComponentProps, Switch } from 'react-router-dom';
import { Dispatch } from 'redux';

import { addFlashMessages } from '../../actions/FlashMessagesActions';
import { fetchAllTeams } from '../../actions/Team/TeamActions';
import { fetchTeamOptions } from '../../actions/Team/TeamOptionsActions';
import PrivateRoute from '../../components/PrivateRoute/PrivateRoute';
import { Loader } from '../../components/Loader';
import { TeamMenu } from '../../components/Team/TeamMenu';
import { localeData } from '../../locales/data';
import { StateType } from '../../reducers';
import { getIsLoggedInSelector } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { activeTeamSelector } from '../../reducers/TeamReducer/ActiveTeamReducer/ActiveTeamSelector';
import {
  dataTeamCompetitionTeamBasedIdSelector,
  dataTeamTerminatedTeamsFirstResultSelector,
} from '../../reducers/TeamReducer/DataTeamReducer/DataTeamSelector';
import { DispatchConnect } from '../../types/';
import { ErrorBoundary } from '../ErrorBoundary';

import { withTeamGuard } from './HOCTeamGuard';

const ActiveTeam = React.lazy(() => import('./ActiveTeam/ActiveTeam'));
const TeamPending = React.lazy(() => import('./TeamPending'));
const PastTeam = React.lazy(() => import('./PastTeam'));

interface TeamProps {}

interface TeamStateProps {
  teamCompetitionId: string;
  isLoggedIn: boolean;
  activeTeam: any;
  terminatedTeam: any;
}

interface TeamStateDispatchers {
  dispatchTerminatedMessage: (userId: string) => void;
  dispatchFetchTeamOptions: (teamCompetitionId?: string) => Promise<object>;
  dispatchFetchTeams: () => Promise<object>;
}

type TeamGenericType = RouteComponentProps<TeamProps> & TeamStateProps & TeamStateDispatchers;

class Team extends React.Component<TeamGenericType> {
  componentDidMount() {
    if (this.props.teamCompetitionId) {
      this.props
        .dispatchFetchTeamOptions(this.props.teamCompetitionId)
        .then(() => {
          this.props
            .dispatchFetchTeams()
            .then(() => this.handleTerminatedTeamMessage())
            .catch();
        })
        .catch();
    }
  }

  handleTerminatedTeamMessage = () => {
    if (!this.props.activeTeam && this.props.terminatedTeam && this.props.terminatedTeam.get('terminator')) {
      const dateTerminated = moment(this.props.terminatedTeam.get('dateTerminated')).add(7, 'days');
      if (dateTerminated.isAfter(moment())) {
        this.props.dispatchTerminatedMessage(this.props.terminatedTeam.get('terminator'));
      }
    }
  };

  render(): JSX.Element {
    if (this.props.location.pathname === '/team') {
      return <Redirect from={this.props.location.pathname} to="/team/active" />;
    } else {
      return (
        <ErrorBoundary showUI={true} isNotFullScreen={true}>
          <section className="ws-main team">
            <TeamMenu />
            <React.Suspense fallback={<Loader active={true} inline="centered" size="massive" />}>
              <Switch>
                <PrivateRoute path="/team/active" exact={true} isAuthenticated={this.props.isLoggedIn} component={ActiveTeam} />
                <PrivateRoute path="/team/invitations" exact={true} isAuthenticated={this.props.isLoggedIn} component={TeamPending} />
                <PrivateRoute path="/team/past" exact={true} isAuthenticated={this.props.isLoggedIn} component={PastTeam} />
                <PrivateRoute path="/team/create" exact={true} isAuthenticated={this.props.isLoggedIn} component={ActiveTeam} />
              </Switch>
            </React.Suspense>
          </section>
        </ErrorBoundary>
      );
    }
  }
}

const mapStateToProps = (state: StateType): TeamStateProps => ({
  isLoggedIn: getIsLoggedInSelector(state) as boolean,
  // @ts-ignore
  teamCompetitionId: dataTeamCompetitionTeamBasedIdSelector(state),
  activeTeam: activeTeamSelector(state),
  terminatedTeam: dataTeamTerminatedTeamsFirstResultSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): TeamStateDispatchers => ({
  dispatchFetchTeamOptions: async (competitionId: string = 'CONSULTANT') => dispatch(fetchTeamOptions(competitionId)),
  dispatchFetchTeams: async () => dispatch(fetchAllTeams()),
  dispatchTerminatedMessage: (userId: string) =>
    dispatch(
      addFlashMessages([
        { msg: 'team.terminated.message', defaultMessage: localeData.en['team.terminated.message'], intlValues: { userId } },
      ])
    ),
});

export default withRouter(
  connect<TeamStateProps, DispatchConnect<TeamStateDispatchers>, any, StateType>(mapStateToProps, mapDispatchToProps)(withTeamGuard(Team))
);
